import { PocSwitch } from 'commons/components/components-wrapper/poc-switch.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { useTranslation } from 'core/i18n/i18n';
import {
    PredefinedCommentFormCard,
    PredefinedCommentFormCardContent,
    PredefinedCommentFormFieldRow,
    PredefinedCommentFormFieldRowLabel,
    PredefinedCommentOwcInput,
} from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-form/settings-devices-predefined-comments-form.style';
import { usePredefinedCommentValidation } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-form/hooks/use-predefined-comment-validation.hook';
import { usePredefinedCommentErrorMessagesForm } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-form/hooks/use-predefined-comment-error-messages-form.hook';
import { useEffect, useRef } from 'react';
import {
    DEFAULT_COMMENT_LENGTH,
    MAX_COMMENT_LENGTH_WHEN_MARKED_AS_ERRONEOUS,
} from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-form/settings-devices-predefined-comments-form.constants';

export interface PredefinedCommentFormInfoProps {
    commentText: string;
    active: boolean;
    hasAnyErroneous: boolean;
    onCommentTextChange: (value: string | null) => void;
    onActiveChange: (value: boolean) => void;
}

export const PredefinedCommentFormInfo: React.FC<PredefinedCommentFormInfoProps> = ({
    commentText,
    active,
    hasAnyErroneous,
    onCommentTextChange,
    onActiveChange,
}) => {
    const { t } = useTranslation();
    const { validity, handleError, getCommentTextValidity } = usePredefinedCommentValidation();
    const { errorMessages } = usePredefinedCommentErrorMessagesForm();
    const isFirstRender = useRef(true);

    const handleCommentTextValidity = (value: string | null) => {
        const newValidity = getCommentTextValidity(value, hasAnyErroneous);
        handleError('commentText', newValidity);
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        const newValidity = getCommentTextValidity(commentText, hasAnyErroneous);
        handleError('commentText', newValidity);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasAnyErroneous]);

    return (
        <PredefinedCommentFormCard>
            <PredefinedCommentFormCardContent>
                <PredefinedCommentOwcInput
                    name="commentText"
                    label="settings.devices.predefined-comments.form.comment-text.label"
                    value={commentText}
                    floatingAssistiveText
                    maxlength={hasAnyErroneous ? MAX_COMMENT_LENGTH_WHEN_MARKED_AS_ERRONEOUS : DEFAULT_COMMENT_LENGTH}
                    required
                    autofocus
                    validity={validity.commentText}
                    validationMessages={errorMessages.commentText}
                    placeholder="settings.devices.predefined-comments.form.comment-text.placeholder"
                    onChange={(_, value) => {
                        handleCommentTextValidity(value);
                        onCommentTextChange(value ?? '');
                    }}
                />
                <PredefinedCommentFormFieldRow>
                    <PredefinedCommentFormFieldRowLabel>
                        <PocTypography variant="subtitle2">
                            {t('settings.devices.predefined-comments.form.set-active.label')}
                        </PocTypography>
                        <PocTypography variant="caption">
                            {t('settings.devices.predefined-comments.form.set-active.description')}
                        </PocTypography>
                    </PredefinedCommentFormFieldRowLabel>
                    <PocSwitch
                        data-testid="active-switch"
                        checked={active}
                        onValueChange={() => onActiveChange(!active)}
                    />
                </PredefinedCommentFormFieldRow>
            </PredefinedCommentFormCardContent>
        </PredefinedCommentFormCard>
    );
};
